import React, { useRef } from 'react';
import { Desktop, Mobile } from '../../App';
import { P1, P3 } from '../../styles/FontStyles';
import {
  LogoContainer,
  ProgressBarBackground,
  ProgressBarFill,
  ProgressContainer,
  SegmentLabel,
  SegmentMarker,
  segments
} from './EarnBet.styled';

interface ProgressBarProps {
  current: number;
  max: number;
  onChange: (val: number) => void;
}

export const allowedValues: number[] = [];

const pushRange = (start: number, end: number, step: number) => {
  for (let i = start; i <= end; i += step) {
    allowedValues.push(i);
  }
};

pushRange(0, 100, 5);
pushRange(110, 250, 10);
pushRange(260, 500, 10);
pushRange(525, 1000, 25);
pushRange(1200, 5000, 200);
pushRange(6000, 25000, 1000);
pushRange(26000, 50000, 1000);

const getClosestValue = (val: number) => {
  let closest = allowedValues[0];
  let diff = Math.abs(val - closest);
  for (let i = 1; i < allowedValues.length; i++) {
    const newDiff = Math.abs(val - allowedValues[i]);
    if (newDiff < diff) {
      diff = newDiff;
      closest = allowedValues[i];
    }
  }
  return closest;
};

const getSegmentIndexForValue = (value: number): number => {
  for (let i = 0; i < segments.length - 1; i++) {
    const start = segments[i].value;
    const end = segments[i + 1].value;
    if (value >= start && value <= end) {
      return i;
    }
  }
  return segments.length - 2;
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  max,
  onChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);

  const segmentCount = segments.length - 1;
  const segmentWidth = 100 / segmentCount;

  const segmentIndex = getSegmentIndexForValue(current);
  const segmentStart = segments[segmentIndex].value;
  const segmentEnd = segments[segmentIndex + 1].value;
  const segmentRange = segmentEnd - segmentStart;
  const normalized = (current - segmentStart) / segmentRange;
  const segmentProgress = segmentWidth * normalized;
  const totalProgress = segmentWidth * segmentIndex + segmentProgress;

  const handleMove = (clientX: number) => {
    if (!isDragging.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const percent = Math.max(0, Math.min(offsetX / rect.width, 1));

    const clampedIndex = Math.min(
      Math.floor(percent * segmentCount),
      segments.length - 2
    );
    const segmentStartValue = segments[clampedIndex].value;
    const segmentEndValue = segments[clampedIndex + 1].value;

    const segmentX = percent * 100 - segmentWidth * clampedIndex;
    const localPercent = segmentX / segmentWidth;

    const rawValue =
      segmentStartValue + localPercent * (segmentEndValue - segmentStartValue);
    const snapped = getClosestValue(rawValue);

    onChange(snapped);
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault(); // prevent scroll while dragging
    handleMove(e.touches[0].clientX);
  };

  const stopDragging = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopDragging);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', stopDragging);
  };

  const handleLogoMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopDragging);
  };

  const handleLogoTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', stopDragging);
  };

  return (
    <ProgressContainer ref={containerRef}>
      <ProgressBarBackground />
      <ProgressBarFill progress={totalProgress} />

      {segments.map((segment, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `calc(${(index / segmentCount) * 100}% - 1px)`,
            top: '0'
          }}>
          <SegmentLabel index={index}>
            <Desktop>
              <P1 semiBold color='#007D85'>
                {segment.label}
              </P1>
            </Desktop>
            <Mobile>
              <P3 semiBold color='#007D85'>
                {segment.labelMobile}
              </P3>
            </Mobile>
          </SegmentLabel>
          {index > 0 && index + 1 < segments.length && <SegmentMarker />}
        </div>
      ))}

      <LogoContainer
        progress={totalProgress}
        onMouseDown={handleLogoMouseDown}
        onTouchStart={handleLogoTouchStart}
        style={{ cursor: 'grab' }}>
        <Desktop>
          <svg
            width='34'
            height='38'
            viewBox='0 0 34 38'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M7.30263 0.860966C6.5984 0.860966 6.00346 1.40665 5.88204 2.15197L0.393945 36.0243C0.236101 36.9825 0.91604 37.8477 1.81453 37.8477H18.0117C21.715 37.8477 24.7019 36.9692 26.9481 35.2124C29.2065 33.4556 30.6028 30.9534 31.1491 27.7192C31.2463 27.1336 31.2949 26.548 31.2949 25.989C31.2949 24.2854 30.8456 22.7815 29.935 21.4905C29.0365 20.2128 27.7494 19.3343 26.0739 18.8818C27.8709 18.3894 29.425 17.4045 30.712 15.9272C31.9991 14.4498 32.8247 12.6264 33.1768 10.457C33.2982 9.55197 33.3711 8.88651 33.3711 8.47392C33.3711 5.95845 32.509 4.06852 30.7849 2.77751C29.0607 1.48651 26.6445 0.847656 23.512 0.847656H7.30263V0.860966ZM12.2565 23.9394C11.4551 23.8995 10.7388 23.62 10.1681 22.9944C9.76742 22.5552 9.53672 22.0228 9.47601 21.4239C9.39102 20.6387 9.57315 19.9333 9.88884 19.2279C10.2167 18.4959 10.6659 17.857 11.188 17.2847C12.4022 15.9005 13.5435 14.4498 14.5999 12.9192C14.782 12.6531 14.9641 12.4002 15.1462 12.134C15.1462 12.134 15.1462 12.134 15.1584 12.1074C15.1584 12.1074 15.1827 12.1074 15.1948 12.1074L15.2069 12.134C15.3891 12.4002 15.5712 12.6531 15.7533 12.9192C16.8097 14.4498 17.951 15.9005 19.1652 17.2847C19.6751 17.8703 20.1244 18.5092 20.4644 19.2279C20.7679 19.92 20.9622 20.6387 20.8772 21.4239C20.8165 22.0361 20.5858 22.5685 20.1851 22.9944C19.6023 23.62 18.8859 23.8995 18.0967 23.9394C17.2589 23.9793 16.4818 23.7397 15.7655 23.2872C15.7412 23.2739 15.7169 23.2606 15.6683 23.234C15.6805 23.3937 15.6926 23.5401 15.7169 23.6865C15.8019 24.1523 15.9719 24.5782 16.1904 24.9908C16.4576 25.4966 16.749 26.0023 17.0282 26.5081C17.0404 26.5347 17.0525 26.5613 17.0646 26.6013C16.4454 26.6013 13.9685 26.6013 13.3371 26.6013C13.3493 26.5613 13.3614 26.5347 13.3735 26.5081C13.6528 26.0023 13.9321 25.5099 14.2113 24.9908C14.4299 24.5782 14.5999 24.1523 14.6849 23.6865C14.7091 23.5401 14.7213 23.407 14.7334 23.234C14.6849 23.2606 14.6606 23.2739 14.6363 23.2872C13.9685 23.6998 13.2521 23.9394 12.4872 23.9394C12.4265 23.9394 12.3658 23.9394 12.3051 23.9394H12.2565Z'
              fill='#063549'
            />
          </svg>
        </Desktop>
        <Mobile>
          <svg
            width='20'
            height='24'
            viewBox='0 0 34 38'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M7.30263 0.860966C6.5984 0.860966 6.00346 1.40665 5.88204 2.15197L0.393945 36.0243C0.236101 36.9825 0.91604 37.8477 1.81453 37.8477H18.0117C21.715 37.8477 24.7019 36.9692 26.9481 35.2124C29.2065 33.4556 30.6028 30.9534 31.1491 27.7192C31.2463 27.1336 31.2949 26.548 31.2949 25.989C31.2949 24.2854 30.8456 22.7815 29.935 21.4905C29.0365 20.2128 27.7494 19.3343 26.0739 18.8818C27.8709 18.3894 29.425 17.4045 30.712 15.9272C31.9991 14.4498 32.8247 12.6264 33.1768 10.457C33.2982 9.55197 33.3711 8.88651 33.3711 8.47392C33.3711 5.95845 32.509 4.06852 30.7849 2.77751C29.0607 1.48651 26.6445 0.847656 23.512 0.847656H7.30263V0.860966ZM12.2565 23.9394C11.4551 23.8995 10.7388 23.62 10.1681 22.9944C9.76742 22.5552 9.53672 22.0228 9.47601 21.4239C9.39102 20.6387 9.57315 19.9333 9.88884 19.2279C10.2167 18.4959 10.6659 17.857 11.188 17.2847C12.4022 15.9005 13.5435 14.4498 14.5999 12.9192C14.782 12.6531 14.9641 12.4002 15.1462 12.134C15.1462 12.134 15.1462 12.134 15.1584 12.1074C15.1584 12.1074 15.1827 12.1074 15.1948 12.1074L15.2069 12.134C15.3891 12.4002 15.5712 12.6531 15.7533 12.9192C16.8097 14.4498 17.951 15.9005 19.1652 17.2847C19.6751 17.8703 20.1244 18.5092 20.4644 19.2279C20.7679 19.92 20.9622 20.6387 20.8772 21.4239C20.8165 22.0361 20.5858 22.5685 20.1851 22.9944C19.6023 23.62 18.8859 23.8995 18.0967 23.9394C17.2589 23.9793 16.4818 23.7397 15.7655 23.2872C15.7412 23.2739 15.7169 23.2606 15.6683 23.234C15.6805 23.3937 15.6926 23.5401 15.7169 23.6865C15.8019 24.1523 15.9719 24.5782 16.1904 24.9908C16.4576 25.4966 16.749 26.0023 17.0282 26.5081C17.0404 26.5347 17.0525 26.5613 17.0646 26.6013C16.4454 26.6013 13.9685 26.6013 13.3371 26.6013C13.3493 26.5613 13.3614 26.5347 13.3735 26.5081C13.6528 26.0023 13.9321 25.5099 14.2113 24.9908C14.4299 24.5782 14.5999 24.1523 14.6849 23.6865C14.7091 23.5401 14.7213 23.407 14.7334 23.234C14.6849 23.2606 14.6606 23.2739 14.6363 23.2872C13.9685 23.6998 13.2521 23.9394 12.4872 23.9394C12.4265 23.9394 12.3658 23.9394 12.3051 23.9394H12.2565Z'
              fill='#063549'
            />
          </svg>
        </Mobile>
      </LogoContainer>
    </ProgressContainer>
  );
};
